import { Modal, ModalBody, ModalContent, ModalOverlay, ModalCloseButton } from '@chakra-ui/modal';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Card from 'components/card';
import { MdCheckCircle, MdStore } from 'react-icons/md';
import CustomerSelection from './CustomerSelection';
import ProductSelection from './ProductSelection';
import SignatureStep from './SignatureStep';
import { createOrder, updateOrder } from 'interfaces/order';
import { signDelivery } from 'interfaces/delivery';
import { generateInvoiceForSpecificOrder, getNotPaidInvoicedPerCustomer } from 'interfaces/invoice';
import { getMyInfo } from 'interfaces/user';
import { getTotalHT } from 'views/orders/createOrders/utils';
import { FinalizationOptions } from './SignatureStep';

interface QuickOrderProps {
    isOpen: boolean;
    onClose: () => void;
    onOrderCreated?: () => void;
}

const QuickOrder = ({ isOpen, onClose, onOrderCreated }: QuickOrderProps) => {
    const [step, setStep] = useState(1);
    const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
    const [orderItems, setOrderItems] = useState<any[]>([]);
    const [orderId, setOrderId] = useState<string | null>(null);
    const [signature, setSignature] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [userCompany, setUserCompany] = useState<string>('');
    const [pendingInvoicesSummary, setPendingInvoicesSummary] = useState<{
        count: number;
        totalLeftToPay: number;
        oldestDaysOverdue: number | null;
    } | null>(null);

    useEffect(() => {
        if (isOpen) {
            if (!userCompany) {
                getMyInfo().then((res) => {
                    if (res?.data?.company) setUserCompany(res.data.company);
                });
            }
        } else {
            // Reset state when modal closes
            setTimeout(() => {
                setStep(1);
                setSelectedCustomer(null);
                setOrderItems([]);
                setOrderId(null);
                setSignature(null);
                setIsProcessing(false);
                setPendingInvoicesSummary(null);
            }, 300);
        }
    }, [isOpen]);

    const handleCustomerSelected = async (customer: any) => {
        setSelectedCustomer(customer);
        setStep(2);
        // Charge invoices unpaid
        try {
            const res = await getNotPaidInvoicedPerCustomer(1000, 1, customer._id);
            if (res && !res.error && res.data?.data) {
                const invoices = res.data.data;
                const count = invoices.length;
                const totalLeftToPay = invoices.reduce((acc: number, inv: any) => {
                    return acc + Math.max(0, Number(inv.leftToPay) || 0);
                }, 0);
                let oldestDate: Date | null = null;
                for (const inv of invoices) {
                    const refDate = inv.dueDate || inv.invoicedAt || inv.createdAt;
                    if (refDate) {
                        const d = new Date(refDate);
                        if (!oldestDate || d < oldestDate) oldestDate = d;
                    }
                }
                const today = new Date();
                const oldestDaysOverdue =
                    oldestDate !== null
                        ? Math.floor(
                              (today.getTime() - oldestDate.getTime()) /
                                  (1000 * 60 * 60 * 24),
                          )
                        : null;
                setPendingInvoicesSummary(count > 0 ? { count, totalLeftToPay, oldestDaysOverdue } : null);
            } else {
                setPendingInvoicesSummary(null);
            }
        } catch {
            setPendingInvoicesSummary(null);
        }
    };

    const handleBackToCustomerSelection = () => {
        setStep(1);
    };

    const handleProductsConfirmed = (products: any[]) => {
        setOrderItems(products);
        setStep(3);
    };

    const createOrderAtFinalStep = async (): Promise<string> => {
        if (orderId) return orderId;
        if (!selectedCustomer?._id) throw new Error('Client manquant');
        if (!orderItems || orderItems.length === 0)
            throw new Error('Aucun produit dans la commande');

        const orderData = {
            customerId: selectedCustomer._id,
            items: orderItems.map((p: any) => ({
                productId: p._id || p.productId,
                name: p.name || p.productName,
                quantity: p.quantity,
                price: p.price,
                discountType: p.discountType || 'none',
                discountValue: p.discountValue || 0,
                comment: p.comment || '',
                lotNumber: p.lotNumber || '',
                unity: p.unity || p.packagingUnity || 'unité',
                ref: p.ref,
                packagingRef: p.packagingRef,
                packagingUnity: p.packagingUnity,
                packagingWeight: p.packagingWeight,
            })),
            totalPrice: getTotalHT(orderItems),
            status: 'pending',
            deliveryDate: new Date().toISOString(),
            isCashAndCarry: true,
        };

        const createdOrder = await createOrder(orderData);
        if (!createdOrder || !createdOrder._id)
            throw new Error('Échec de la création de la commande');

        setOrderId(createdOrder._id);
        toast.success(`Commande n°${createdOrder.orderId} créée`);
        return createdOrder._id;
    };

    const handleSignatureComplete = async (
        signatureData: string,
        options: FinalizationOptions,
    ) => {
        setSignature(signatureData);
        await finalizeOrder(signatureData, options);
    };

    const finalizeOrder = async (
        signatureData: string,
        options: FinalizationOptions,
    ) => {
        try {
            setIsProcessing(true);

            const finalOrderId = await createOrderAtFinalStep();

            // Signature flow used in deliveries: upload signature file to R2
            // so it can appear on generated delivery notes.
            if (signatureData) {
                const blob = await fetch(signatureData).then((res) => res.blob());
                const formData = new FormData();
                formData.append('file', blob, 'signature.png');
                const signRes = await signDelivery(formData, finalOrderId, false);
                if (signRes?.error) {
                    throw new Error('Échec de l\'enregistrement de la signature');
                }
                toast.success('Signature enregistrée');
            } else {
                // No signature: still mark order as delivered
                await updateOrder({
                    _id: finalOrderId,
                    status: 'delivered',
                    deliveredAt: new Date().toISOString(),
                });
            }

            if (!options.shouldInvoice) {
                toast.success('Commande finalisée sans facturation');
                setTimeout(() => {
                    onClose();
                    if (onOrderCreated) {
                        onOrderCreated();
                    }
                }, 1000);
                return;
            }

            // Generate invoice (and optionally register payment/send email) in one backend call
            const invoice = await generateInvoiceForSpecificOrder(finalOrderId, {
                registerPayment: options.registerPayment,
                paymentAmount: Number(options.paymentAmount),
                paymentMethod: options.paymentMethod,
                paymentReference: options.paymentReference,
                paymentDate: new Date().toISOString().split('T')[0],
                sendEmail: options.sendInvoiceByEmail,
                emailName: options.emailSubject,
                emailContent: options.emailContent,
            });

            const generatedInvoiceId = invoice?.data?._id || invoice?.data?.invoiceId;

            if (generatedInvoiceId) {
                if (options.registerPayment && invoice?.data?.paymentRegistered)
                    toast.success('Règlement enregistré sur la facture');
                else if (options.registerPayment)
                    toast.error('Facture créée mais règlement non enregistré');

                if (options.sendInvoiceByEmail && invoice?.data?.emailSent)
                    toast.success('Facture générée et envoyée par email');
                else if (options.sendInvoiceByEmail)
                    toast.error('Facture générée mais envoi email impossible');
                else toast.success('Facture générée');
            } else if (invoice?.data?.success) {
                toast.success('Facture générée');
            } else {
                toast.warning('Commande finalisée mais erreur lors de la génération de la facture');
            }

            // Success - close modal after a short delay
            setTimeout(() => {
                onClose();
                if (onOrderCreated) {
                    onOrderCreated();
                }
            }, 1500);

        } catch (error) {
            console.error('Error finalizing order:', error);
            toast.error('Erreur lors de la finalisation de la commande');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleSkipSignature = async (options: FinalizationOptions) => {
        await finalizeOrder('', options);
    };

    const getStepTitle = () => {
        switch (step) {
            case 1:
                return 'Sélectionner le client';
            case 2:
                return 'Ajouter les produits';
            case 3:
                return 'Signature du bon de livraison';
            default:
                return 'Commande rapide';
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="6xl">
            <ModalOverlay className="bg-[#000] !opacity-30" />
            <ModalContent className="!z-[1002] !my-4 !mx-auto !w-[95vw] md:!w-[90vw] lg:!w-[1100px] !max-w-[95vw] !max-h-[92vh] flex flex-col overflow-hidden md:top-[5vh]">
                <ModalCloseButton 
                    className="!text-gray-600 hover:!text-gray-800"
                    isDisabled={isProcessing}
                />
                <ModalBody className="p-0 overflow-y-auto">
                    <Card extra="px-6 md:px-[30px] pt-[35px] pb-[40px] flex flex-col !z-[1004]">
                        <div className="mb-6 flex items-center gap-3">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-toola-500">
                                <MdStore className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
                                    {getStepTitle()}
                                </h1>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Commande Cash & Carry - Étape {step}/3
                                </p>
                            </div>
                        </div>

                        {/* Progress bar */}
                        <div className="mb-6 flex gap-2">
                            <div className={`h-2 flex-1 rounded-full ${step >= 1 ? 'bg-toola-500' : 'bg-gray-200'}`} />
                            <div className={`h-2 flex-1 rounded-full ${step >= 2 ? 'bg-toola-500' : 'bg-gray-200'}`} />
                            <div className={`h-2 flex-1 rounded-full ${step >= 3 ? 'bg-toola-500' : 'bg-gray-200'}`} />
                        </div>

                        <div className="min-h-[400px]">
                            {pendingInvoicesSummary && step > 1 && (
                                <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg flex flex-wrap gap-4 items-center">
                                    <p className="text-xs font-semibold text-orange-700">
                                        ⚠️ {pendingInvoicesSummary.count} facture{pendingInvoicesSummary.count > 1 ? 's' : ''} en attente de paiement
                                    </p>
                                    <p className="text-xs text-gray-600">
                                        À régler :{' '}
                                        <span className="font-bold text-orange-600">
                                            {pendingInvoicesSummary.totalLeftToPay.toFixed(2)}€
                                        </span>
                                    </p>
                                    {pendingInvoicesSummary.oldestDaysOverdue !== null && (
                                        <p className="text-xs text-gray-600">
                                            Retard (+ ancienne) :{' '}
                                            <span className={`font-bold ${
                                                pendingInvoicesSummary.oldestDaysOverdue > 30
                                                    ? 'text-red-600'
                                                    : 'text-orange-600'
                                            }`}>
                                                {pendingInvoicesSummary.oldestDaysOverdue} jour{pendingInvoicesSummary.oldestDaysOverdue > 1 ? 's' : ''}
                                            </span>
                                        </p>
                                    )}
                                </div>
                            )}
                            {step === 1 && (
                                <CustomerSelection
                                    onCustomerSelected={handleCustomerSelected}
                                    isProcessing={isProcessing}
                                />
                            )}

                            {step === 2 && (
                                <ProductSelection
                                    customer={selectedCustomer}
                                    initialSelectedProducts={orderItems}
                                    onBack={handleBackToCustomerSelection}
                                    onConfirm={handleProductsConfirmed}
                                    isProcessing={isProcessing}
                                />
                            )}

                            {step === 3 && (
                                <SignatureStep
                                    orderId={orderId}
                                    customer={selectedCustomer}
                                    orderItems={orderItems}
                                    sellerCompany={userCompany}
                                    onSignatureComplete={handleSignatureComplete}
                                    onSkip={handleSkipSignature}
                                    onBack={() => setStep(2)}
                                    isProcessing={isProcessing}
                                />
                            )}
                        </div>
                    </Card>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default QuickOrder;
